import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const GENDER_LABELS = { male: "Male", female: "Female", neutral: "Neutral" };

const AccountRow = ({ label, children, testid }) => (
  <div className="flex items-center justify-between gap-5 py-5 border-b border-violet-200/10 last:border-b-0">
    <span className="text-xs uppercase tracking-[0.28em] text-violet-300/55">{label}</span>
    <span className="text-violet-50/90 text-sm text-right" data-testid={testid}>{children}</span>
  </div>
);

const DangerRow = ({ label, description, actionLabel, onAction, testid, tone = "danger", disabled }) => (
  <div className="py-4 border-b border-violet-200/10 last:border-b-0">
    <div className="flex items-center justify-between gap-4">
      <div className="min-w-0">
        <p className="text-violet-50/90 text-sm">{label}</p>
        {description && (
          <p className="mt-1 text-xs text-violet-300/55 leading-relaxed">{description}</p>
        )}
      </div>
      <button
        type="button"
        onClick={onAction}
        disabled={disabled}
        data-testid={testid}
        className={`shrink-0 rounded-full px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-soft ${
          tone === "danger"
            ? "border-rose-300/40 text-rose-200 hover:border-rose-300/70 hover:text-rose-100 hover:bg-rose-500/10"
            : "border-violet-300/40 text-violet-100 hover:border-violet-200/70 hover:bg-violet-500/10"
        } disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {actionLabel}
      </button>
    </div>
  </div>
);

const CONFIRMATIONS = {
  "delete-last": {
    title: "Delete your most recent session?",
    description:
      "The last evening (and its morning, if any) will be removed. She will not remember it happened. This cannot be undone.",
    action: "Delete last session",
  },
  "delete-all": {
    title: "Delete every saved session?",
    description:
      "All sessions with this companion will be permanently removed. Memories linked to them go too. The relationship counter resets. This cannot be undone.",
    action: "Delete all sessions",
  },
  "reset-relationship": {
    title: "Reset your relationship with her?",
    description:
      "Every session, memory, and shared moment will be erased. Your next meeting will feel like the very first time again. Her personality remains — only the relationship resets. This cannot be undone.",
    action: "Reset relationship",
  },
  "delete-account": {
    title: "Permanently delete your account?",
    description:
      "Your account, every session, every memory, and every trace of your journey will be erased. This cannot be undone.",
    action: "Delete account",
  },
};

export default function Settings() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const [pending, setPending] = useState(null); // key of CONFIRMATIONS
  const [busy, setBusy] = useState(false);
  const [savingGender, setSavingGender] = useState(false);
  const [sub, setSub] = useState(null); // Iter 88 — /api/subscription
  // Iter 98 — change password
  const [pwOpen, setPwOpen] = useState(false);
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwSaving, setPwSaving] = useState(false);

  const submitPasswordChange = async (e) => {
    e.preventDefault();
    if (pwNew !== pwConfirm) {
      toast.error("New passwords don't match.");
      return;
    }
    setPwSaving(true);
    try {
      await api.patch("/auth/change-password", { current_password: pwCurrent, new_password: pwNew });
      toast.success("Password updated.");
      setPwOpen(false);
      setPwCurrent(""); setPwNew(""); setPwConfirm("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update password.");
    } finally {
      setPwSaving(false);
    }
  };

  useEffect(() => {
    api.get("/subscription").then((r) => setSub(r.data)).catch(() => setSub(null));
  }, []);

  const currentGender = user?.user_gender || null;

  const setGender = async (g) => {
    if (savingGender || g === currentGender) return;
    setSavingGender(true);
    try {
      await api.patch("/profile/gender", { user_gender: g });
      await refresh();
      toast("Preference updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update preference.");
    } finally {
      setSavingGender(false);
    }
  };

  const runAction = async () => {
    if (!pending || busy) return;
    setBusy(true);
    try {
      if (pending === "delete-last") {
        const { data } = await api.delete("/sessions/last");
        toast(data.deleted?.length ? "The last session has been forgotten." : "There was nothing recent to delete.");
      } else if (pending === "delete-all") {
        const { data } = await api.delete("/sessions/all");
        toast(`${data.deleted_count} session${data.deleted_count === 1 ? "" : "s"} removed.`);
      } else if (pending === "reset-relationship") {
        await api.post("/relationship/reset");
        toast("She'll meet you as if for the very first time.");
      } else if (pending === "delete-account") {
        await api.delete("/account");
        toast("Your account has been erased. Goodbye.");
        logout();
        nav("/", { replace: true });
        return;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
      setPending(null);
    }
  };

  const active = pending ? CONFIRMATIONS[pending] : null;

  return (
    <AppShell>
      <div className="min-h-[calc(100vh-12rem)] max-w-md mx-auto px-6 pt-8 pb-14" data-testid="account-page">
        <p className="text-xs uppercase tracking-[0.32em] text-violet-300/55 mb-4">Account</p>
        <h1 className="font-serif-display text-5xl leading-none text-violet-50 mb-10">You</h1>

        <section className="glass rounded-2xl px-5 mb-10" data-testid="account-summary-card">
          <AccountRow label="Name" testid="account-name-value">
            {user?.user_name || "—"}
          </AccountRow>
          <AccountRow label="Password" testid="account-password-value">
            <button
              type="button"
              onClick={() => setPwOpen((v) => !v)}
              data-testid="change-password-toggle-button"
              className="rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] border border-violet-300/40 text-violet-100 hover:border-violet-200/70 hover:bg-violet-500/10 transition-soft"
            >
              {pwOpen ? "Close" : "Change"}
            </button>
          </AccountRow>
          {pwOpen && (
            <form onSubmit={submitPasswordChange} className="py-5 border-b border-violet-200/10 space-y-4" data-testid="change-password-form">
              {[
                { label: "Current password", value: pwCurrent, set: setPwCurrent, testid: "change-password-current-input" },
                { label: "New password", value: pwNew, set: setPwNew, testid: "change-password-new-input" },
                { label: "Confirm new password", value: pwConfirm, set: setPwConfirm, testid: "change-password-confirm-input" },
              ].map((f) => (
                <div key={f.testid}>
                  <label className="text-xs uppercase tracking-[0.25em] text-violet-300/70 mb-2 block">{f.label}</label>
                  <input
                    type="password" required minLength={f.testid === "change-password-current-input" ? 1 : 6}
                    value={f.value} onChange={(e) => f.set(e.target.value)}
                    data-testid={f.testid}
                    className="w-full bg-indigo-950/40 border border-violet-300/20 rounded-xl px-4 py-2.5 text-sm text-violet-50 focus:outline-none focus:border-violet-300/50 focus:ring-1 focus:ring-violet-300/40 transition-soft"
                    placeholder="••••••••"
                  />
                </div>
              ))}
              <button
                type="submit" disabled={pwSaving}
                data-testid="change-password-submit-button"
                className="w-full rounded-full px-6 py-2.5 bg-violet-200 text-indigo-950 text-sm font-medium hover:bg-violet-100 transition-soft disabled:opacity-50"
              >
                {pwSaving ? "Saving…" : "Update password"}
              </button>
            </form>
          )}
          <AccountRow label="Subscription" testid="account-subscription-value">
            {sub == null ? "…" : sub.source === "developer" ? "Premium · Developer" : (sub.tier_label || "Continua Free").replace("Continua ", "")}
          </AccountRow>
        </section>

        {/* SUBSCRIPTION (Iter 88) ───────────────────────────────────── */}
        <p className="text-xs uppercase tracking-[0.32em] text-violet-300/55 mb-3">Plan</p>
        <section className="glass rounded-2xl px-5 py-5 mb-10" data-testid="subscription-card">
          <p className="text-sm text-violet-50/90 flex items-center gap-2">
            {sub?.tier_label || "Continua Free"}
            {sub?.is_paid && (
              <span className="text-[9px] uppercase tracking-[0.24em] text-violet-100/80 border border-violet-300/35 rounded-full px-2 py-0.5">
                {sub.lifetime ? "Lifetime" : sub.is_trial ? "Trial" : sub.status === "cancelled" ? "Until period end" : "Active"}
              </span>
            )}
          </p>
          {sub && (
            <div className="mt-3 space-y-1.5 text-xs text-violet-300/65" data-testid="subscription-usage">
              <p>
                {sub.remaining?.sessions_this_month == null
                  ? "Unlimited sessions"
                  : `${sub.remaining.sessions_this_month} of ${sub.limits?.monthly_sessions} sessions left this month`}
                {" · "}up to {sub.max_duration_minutes} min each
              </p>
              {sub.is_premium ? (
                <p>{sub.remaining?.morning_sessions_this_month ?? 0} of {sub.limits?.monthly_morning_sessions ?? 3} experimental Morning Sessions left this month</p>
              ) : (
                <p>Morning Sessions (experimental) are part of Continua Premium</p>
              )}
              <p>
                Serine & Elizabeth · {(sub.allowed_atmospheres || []).length} atmosphere{(sub.allowed_atmospheres || []).length === 1 ? "" : "s"}
              </p>
            </div>
          )}
          {!sub?.is_premium && (
            <div className="mt-4 pt-4 border-t border-violet-200/10">
              <p className="text-xs text-violet-300/65 leading-relaxed">
                {sub?.tier === "light"
                  ? "Continua Premium: unlimited sessions, nights up to 90 minutes, and 3 experimental Morning Sessions each month."
                  : "Continua Light opens all 4 atmospheres and 20 sessions a month. Premium removes the limits and adds experimental mornings."}
              </p>
              <button
                type="button"
                onClick={() => nav("/upgrade")}
                data-testid="upgrade-premium-button"
                className="mt-4 inline-flex items-center gap-2 rounded-full px-5 py-2 text-xs uppercase tracking-[0.2em] border border-amber-300/35 text-amber-200/90 hover:bg-amber-300/10 hover:border-amber-300/55 transition-soft"
              >
                View plans
              </button>
            </div>
          )}
        </section>

        {/* GENDER / ADDRESS PREFERENCE ─────────────────────────────────── */}
        <p className="text-xs uppercase tracking-[0.32em] text-violet-300/55 mb-3">Address</p>
        <section className="glass rounded-2xl px-5 py-5 mb-10" data-testid="account-gender-card">
          <p className="text-sm text-violet-100/85">How would you like to be addressed?</p>
          <p className="mt-1 text-xs text-violet-300/55 leading-relaxed mb-4">
            She'll tailor pronouns and terms of endearment to match.
          </p>
          <div className="grid grid-cols-3 gap-2">
            {["male", "female", "neutral"].map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setGender(g)}
                disabled={savingGender}
                data-testid={`account-gender-${g}`}
                className={`rounded-full px-3 py-2 text-xs uppercase tracking-[0.2em] border transition-soft disabled:opacity-60 ${
                  currentGender === g
                    ? "border-violet-200/70 bg-violet-500/10 text-violet-50"
                    : "border-violet-300/20 text-violet-200/80 hover:border-violet-200/40 hover:bg-violet-500/5"
                }`}
              >
                {GENDER_LABELS[g]}
              </button>
            ))}
          </div>
        </section>

        {/* MEMORY MANAGEMENT ─────────────────────────────────────────── */}
        <p className="text-xs uppercase tracking-[0.32em] text-violet-300/55 mb-3">Memory</p>
        <section className="glass rounded-2xl px-5 mb-10" data-testid="memory-management-card">
          <DangerRow
            label="Delete last session"
            description="Removes only the most recent evening. Useful for replaying or correcting a night."
            actionLabel="Delete"
            tone="warn"
            onAction={() => setPending("delete-last")}
            testid="delete-last-session-button"
          />
          <DangerRow
            label="Delete all sessions"
            description="Removes every saved session with this companion. Conversation history becomes empty."
            actionLabel="Delete"
            tone="warn"
            onAction={() => setPending("delete-all")}
            testid="delete-all-sessions-button"
          />
          <DangerRow
            label="Reset relationship"
            description="Wipe memories, session count, and shared history so she meets you as if for the very first time. Personality stays the same."
            actionLabel="Reset"
            tone="danger"
            onAction={() => setPending("reset-relationship")}
            testid="reset-relationship-button"
          />
        </section>

        {/* COMMUNITY moved to the Settings drawer (Iter 89) — the Discord
            entry now lives in the sidebar next to Feedback. */}

        {/* DEVELOPER (Iter 84) — rendered ONLY for role === "developer".
            Normal users get no locked state, no placeholder, nothing. */}
        {user?.role === "developer" && (
          <>
            <p className="mt-10 text-xs uppercase tracking-[0.32em] text-emerald-300/60 mb-3">Internal</p>
            <section className="glass rounded-2xl px-5 mb-10" data-testid="developer-panel-card">
              <button
                type="button"
                onClick={() => nav("/dev")}
                data-testid="developer-panel-link"
                className="w-full flex items-center justify-between gap-4 py-5 text-left"
              >
                <div>
                  <p className="text-violet-50/90 text-sm">Developer Panel</p>
                  <p className="mt-1 text-xs text-violet-300/55">Session diagnostics, generation metrics, prompt inspector.</p>
                </div>
                <span className="text-[10px] uppercase tracking-widest text-emerald-300/60 border border-emerald-300/25 rounded-full px-2 py-0.5">dev</span>
              </button>
            </section>
          </>
        )}

        {/* ACCOUNT DANGER ────────────────────────────────────────────── */}
        <p className="text-xs uppercase tracking-[0.32em] text-rose-300/60 mb-3">Danger zone</p>
        <section className="glass rounded-2xl px-5" data-testid="account-danger-card">
          <DangerRow
            label="Delete account"
            description="Permanently deletes your account, every session, every memory. This cannot be undone."
            actionLabel="Delete account"
            tone="danger"
            onAction={() => setPending("delete-account")}
            testid="delete-account-button"
          />
        </section>
      </div>

      <AlertDialog open={!!pending} onOpenChange={(open) => { if (!open) setPending(null); }}>
        <AlertDialogContent data-testid="settings-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="settings-confirm-title">
              {active?.title}
            </AlertDialogTitle>
            <AlertDialogDescription data-testid="settings-confirm-description">
              {active?.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="settings-confirm-cancel" disabled={busy}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="settings-confirm-action"
              disabled={busy}
              onClick={(e) => { e.preventDefault(); runAction(); }}
              className="bg-rose-500/85 text-rose-50 hover:bg-rose-400"
            >
              {busy ? "Working…" : active?.action}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
